import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, Search, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useScopedAuth } from "@/components/CurrentShopProvider";
import { useStaffList } from "@/hooks/useStaff";
import { useMenus } from "@/hooks/useMasterData";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";

interface BookingSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SearchResult {
  id: string;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  status: string | null;
  staff_name: string | null;
  menu_name: string | null;
}

const BookingSearchDialog = ({ open, onOpenChange }: BookingSearchDialogProps) => {
  const navigate = useNavigate();
  const { authUser } = useScopedAuth();
  const { data: staffList = [] } = useStaffList("active");
  const { data: menus = [] } = useMenus();

  const [customerName, setCustomerName] = useState("");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [memberType, setMemberType] = useState("all");
  const [staffId, setStaffId] = useState("all");
  const [menuId, setMenuId] = useState("all");
  const [phone, setPhone] = useState("");

  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleClear = useCallback(() => {
    setCustomerName("");
    setDateFrom(undefined);
    setDateTo(undefined);
    setMemberType("all");
    setStaffId("all");
    setMenuId("all");
    setPhone("");
    setResults([]);
    setHasSearched(false);
  }, []);

  const handleSearch = useCallback(async () => {
    setIsSearching(true);
    setHasSearched(true);
    try {
      // Build query using v_service_bookings view or bookings + joins
      let query = supabase
        .from("bookings")
        .select(`
          id, date, start_time, end_time, customer_name, customer_phone, status, customer_id, menu_id, staff_id,
          staff:staff_id(staff_name),
          menu:menu_id(menu_name),
          customer:customer_id(furigana, member_type)
        `)
        .is("deleted_at", null)
        .order("date", { ascending: false })
        .order("start_time", { ascending: true })
        .limit(50);

      if (authUser?.shop_id) {
        query = query.eq("shop_id", authUser.shop_id);
      }

      // Customer name / furigana search
      if (customerName.trim()) {
        const term = `%${customerName.trim()}%`;
        query = query.or(`customer_name.ilike.${term}`);
      }

      if (phone.trim()) {
        const phoneTerm = `%${phone.trim()}%`;
        query = query.ilike("customer_phone", phoneTerm);
      }

      if (dateFrom) query = query.gte("date", format(dateFrom, "yyyy-MM-dd"));
      if (dateTo) query = query.lte("date", format(dateTo, "yyyy-MM-dd"));
      if (staffId !== "all") query = query.eq("staff_id", staffId);
      if (menuId !== "all") query = query.eq("menu_id", menuId);

      const { data, error } = await query;
      if (error) throw error;

      // Client-side filters
      let filtered = (data ?? []) as any[];

      // Filter by furigana if customerName contains hiragana
      if (customerName.trim()) {
        const term = customerName.trim().toLowerCase();
        filtered = filtered.filter((b: any) => {
          const nameMatch = b.customer_name?.toLowerCase().includes(term);
          const furiganaMatch = b.customer?.furigana?.toLowerCase().includes(term);
          return nameMatch || furiganaMatch;
        });
      }

      // Filter by member_type
      if (memberType !== "all") {
        filtered = filtered.filter((b: any) => b.customer?.member_type === memberType);
      }

      setResults(
        filtered.map((b: any) => ({
          id: b.id,
          date: b.date,
          start_time: b.start_time,
          end_time: b.end_time,
          customer_name: b.customer_name,
          customer_phone: b.customer_phone,
          status: b.status,
          staff_name: b.staff?.staff_name ?? null,
          menu_name: b.menu?.menu_name ?? null,
        }))
      );
    } catch (err) {
      console.error("Booking search error:", err);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [authUser?.shop_id, customerName, dateFrom, dateTo, memberType, staffId, menuId, phone]);

  const handleResultClick = (result: SearchResult) => {
    if (result.date) {
      navigate(`/?date=${result.date}`);
      onOpenChange(false);
    }
  };

  const formatTime = (ts: string | null) => {
    if (!ts) return "";
    try {
      return format(new Date(ts), "HH:mm");
    } catch {
      return "";
    }
  };

  const statusLabels: Record<string, string> = {
    pending: "仮予約",
    confirmed: "確定",
    completed: "完了",
    cancelled: "キャンセル",
    no_show: "無断キャンセル",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>施術予約検索</DialogTitle>
          <DialogDescription>条件を指定して予約を検索します</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Customer name (furigana) */}
          <div className="space-y-1">
            <Label className="text-xs">お客様姓名（ひらがな）</Label>
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="やまだ たろう"
              className="h-9 text-sm"
            />
          </div>

          {/* Phone */}
          <div className="space-y-1">
            <Label className="text-xs">電話番号</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="090-xxxx-xxxx"
              className="h-9 text-sm"
            />
          </div>

          {/* Date from */}
          <div className="space-y-1">
            <Label className="text-xs">施術日（From）</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full h-9 justify-start text-left text-sm font-normal",
                    !dateFrom && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                  {dateFrom ? format(dateFrom, "yyyy/MM/dd") : "開始日"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={setDateFrom}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Date to */}
          <div className="space-y-1">
            <Label className="text-xs">施術日（To）</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full h-9 justify-start text-left text-sm font-normal",
                    !dateTo && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                  {dateTo ? format(dateTo, "yyyy/MM/dd") : "終了日"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={setDateTo}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Member type */}
          <div className="space-y-1">
            <Label className="text-xs">顧客属性</Label>
            <Select value={memberType} onValueChange={setMemberType}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="全て" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全て</SelectItem>
                <SelectItem value="member">会員</SelectItem>
                <SelectItem value="general">一般</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Staff */}
          <div className="space-y-1">
            <Label className="text-xs">施術スタッフ</Label>
            <Select value={staffId} onValueChange={setStaffId}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="全て" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全て</SelectItem>
                {staffList.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.staff_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Menu */}
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">施術メニュー</Label>
            <Select value={menuId} onValueChange={setMenuId}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="全て" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全て</SelectItem>
                {menus.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.menu_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={handleClear}>
            <X className="w-3.5 h-3.5 mr-1" />
            クリア
          </Button>
          <Button size="sm" onClick={handleSearch} disabled={isSearching}>
            <Search className="w-3.5 h-3.5 mr-1" />
            {isSearching ? "検索中..." : "検索"}
          </Button>
        </div>

        {/* Results */}
        {hasSearched && (
          <ScrollArea className="flex-1 min-h-0 max-h-[300px] border rounded-md">
            {results.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                検索結果がありません
              </div>
            ) : (
              <div className="divide-y divide-border">
                {results.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => handleResultClick(r)}
                    className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs font-medium text-foreground whitespace-nowrap">
                          {r.date ?? "—"}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatTime(r.start_time)}〜{formatTime(r.end_time)}
                        </span>
                        <span className="text-sm font-medium text-foreground truncate">
                          {r.customer_name ?? "—"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {r.menu_name && (
                          <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                            {r.menu_name}
                          </span>
                        )}
                        {r.staff_name && (
                          <span className="text-xs text-muted-foreground">
                            {r.staff_name}
                          </span>
                        )}
                        {r.status && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            {statusLabels[r.status] ?? r.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingSearchDialog;
